import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import ReactDOM from 'react-dom/client';
import { useParams, useSearchParams } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ActionCreatorWithPayload, Dispatch } from '@reduxjs/toolkit';
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { cloneDeep, get, includes, isEmpty, map, some } from 'lodash';

import { RootState, store } from '@v7/form/src/store';
import * as faceapi from 'face-api.js';
import * as sentry from '@sentry/react';
import classNames from 'classnames';
// import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareMinus, faSquarePlus } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useCreateBlockNote } from '@blocknote/react';
import '@blocknote/core/fonts/inter.css';
import {
  useCreateAuthRequestMutation,
  useFetchCandidateDetailsQuery,
  useGetFormInstanceByIdQuery,
  useGetFormInstanceQuery,
  useGetFormStructureQuery,
  useSaveFormInstanceMutation,
} from '@v7/form/src/store/services/updateFormBuilder';
import {
  updateCandidateDetails,
  updateFaceAuthDetails,
  updateFormData,
  updateFormFileData,
  updateFormState,
} from '@v7/form/src/store/actions';
import { Data } from '@v7/form/src/Pages/Editor';
import { getItem, setItem } from '@v7/form/src/utils/store';
import { trackEvent } from '@v7/form/src/utils/mixpanel/mixpanelActions';
import { mixpanelEvents } from '@v7/form/src/utils/mixpanel/mixpanelEvent';
import { DEVICE_TYPE, ERROR_MESSAGES, FORM_TYPE, ROUTE_TYPE, UPLOAD_STATUS } from '@v7/form/src/utils/constant';
import {
  getFormattedSaveData,
  initForm,
  initobj,
  isFieldEnabled,
  postFormData,
} from '@v7/form/src/containers/BlockNoteRenderer/utils/formDataHelpers';
import {
  handleSectionInstances,
  initBlocksRender,
  renderContent,
} from '@v7/form/src/containers/BlockNoteRenderer/utils/renderHelpers';
import SectionGroupRender from '@v7/form/src/containers/BlockNoteRenderer/SectionGroupRender';
import {
  CandidateData,
  DeviceType,
  FieldData,
  FormRendererProps,
  FormTenant,
  FormTenantName,
  LogicProps,
} from '@v7/form/src/containers/BlockNoteRenderer/types';
import { prefillSupportedFields } from '@v7/form/src/containers/BlockNoteRenderer/constants';
import Loader from '@v7/form/src/components/Loader';
import Toggle from '@v7/form/src/components/Popover/MenuPopover/ToggleSwitch';
import { schema } from '@v7/form/src/components/BlockNoteEditor';
import Button from '@v7/form/src/components/NewButton';
import {
  failSaveFormInstanceNotification,
  successSaveFormInstanceNotification,
} from '@v7/form/src/components/Notifications/ToastNotification';
import { customBlocksTypes } from '@v7/form/src/components/Tools/CustomBlocks/utils';
// import { isFieldEnabled } from '@v7/form/src/containers/BlockNoteRenderer/utils/formDataHelpers';

const messages = defineMessages({
  mobileView: {
    id: 'form.blockNoteRenderer.mobileView',
    defaultMessage: 'Mobile View',
  },
  backToEditor: {
    id: 'form.blockNoteRenderer.backToEditor',
    defaultMessage: 'Back to Editor',
  },
  submit: {
    id: 'form.blockNoteRenderer.submit',
    defaultMessage: 'Submit',
  },
});

async function loadModels() {
  try {
    await faceapi.nets.tinyFaceDetector.loadFromUri('/weights');
    await faceapi.nets.faceLandmark68Net.loadFromUri('/weights');
    await faceapi.nets.faceRecognitionNet.loadFromUri('/weights');
  } catch (error) {
    console.error('Error loading models: ', error);
  }
}

export const processFaceAuthDetails = async (
  faceAuthDetails: FieldData,
  dispatch: Dispatch,
  createAuthRequest: MutationTrigger<any>,
  updateFaceAuthDetails: ActionCreatorWithPayload<Partial<FieldData>>,
) => {
  if (isEmpty(faceAuthDetails)) return false;
  let hasError = false;
  await Promise.all(
    map(faceAuthDetails, async (details, key) => {
      try {
        const response = await createAuthRequest(details).unwrap();
        const error_message = get(response, 'ca_create_auth_request.error_message');

        if (error_message) {
          hasError = true;
          dispatch(
            updateFaceAuthDetails({
              [key]: {
                ...details,
                error: error_message,
              },
            }),
          );
        }
      } catch {
        hasError = true;
        dispatch(
          updateFaceAuthDetails({
            [key]: { ...details, error: ERROR_MESSAGES.FACE_AUTH },
          }),
        );
      }
    }),
  );
  return hasError;
};

export const loadSavedContent = (
  content: Array<Record<string, any>>,
  form_instance_data: FieldData,
  candidateDetails: FieldData,
) => {
  try {
    const formData: FieldData = {};
    const formFileData: FieldData = {};
    const savedContent = map(cloneDeep(content), (block) => {
      const attrKey = block?.props?.attrkey;
      const blockId = block?.id;
      const type = block?.type;
      if (attrKey && includes(prefillSupportedFields, type)) {
        const value = form_instance_data?.[blockId] || candidateDetails?.[attrKey];
        if (value) {
          block.props.value = value;
          block.props.pre_filled = true;
          (type === customBlocksTypes.file ? formFileData : formData)[blockId] = value;
        }
      }
      return block;
    });
    return { savedContent, formData, formFileData };
  } catch (error) {
    console.log('load saved content error', error);
    return { savedContent: content };
  }
};

export const renderIcon = (icon: IconProp, isDisabled: boolean, onClickFn: () => void) => (
  <FontAwesomeIcon
    icon={icon}
    onClick={() => !isDisabled && onClickFn?.()}
    className={classNames('mx-2', isDisabled ? 'cursor-not-allowed text-gray-500' : 'cursor-pointer text-blue-600')}
    size="2xl"
  />
);

export const handleFormDetails = async (response: FormTenant): Promise<FormTenantName | null> => {
  try {
    if (!response?.tenant?.name || !response?.form_name) {
      throw new Error('Missing required form details');
    }

    const formTenantName: FormTenantName = {
      tenant_name: response?.tenant?.name,
      form_name: response?.form_name,
    };

    await setItem('form_tenant_name', formTenantName);

    return formTenantName;
  } catch (error) {
    sentry.captureException(error);
    console.error('Error processing form details:', error);
    return null;
  }
};

const BlockNoteRenderer: React.FC<FormRendererProps> = ({ setFormState, formType, formContent }) => {
  const intl = useIntl();
  const { id } = useParams() || {};
  const [searchParams] = useSearchParams();
  // Backend's `model` enum (sch_meeting_participant | workflow_candidate_step) rejects the old
  // `sch_model` default with a 422 — send null until the backend relaxes it back to nullish.
  const model = searchParams.get('model') || null;
  const model_id = searchParams.get('model_id') || 0;
  const form_instance_id = searchParams.get('form_instance_id');
  const isReadOnly = searchParams.get('read_only') === 'true' || searchParams.get('readOnly') === 'true';
  const dispatch = useDispatch();
  const { formData, formFileData, faceAuthDetails, fileUploadStatus } =
    useSelector((state: RootState) => state?.form) || {};
  const editor = useCreateBlockNote({ schema });

  const [deviceType, setDeviceType] = useState<DeviceType>(DEVICE_TYPE.DESKTOP);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupedContent, setGroupedContent] = useState<any>([]);
  const modelId = useRef(model_id);
  const instanceSectionsRef = useRef<FieldData>({});
  const rootRef = useRef<FieldData>({});
  const logicsRef = useRef<LogicProps[]>([]);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const { default_role } = getItem('user_details') || {};

  const [saveForm] = useSaveFormInstanceMutation();
  const { data, status } = useGetFormStructureQuery(
    {
      id,
      state: ROUTE_TYPE.RENDERER,
    },
    { skip: !!formType },
  );

  useEffect(() => {
    if (data) {
      handleFormDetails(data).then((formDetails) => {
        if (formDetails) {
          trackEvent(mixpanelEvents.FORM_LANDED);
        }
      });
    }
  }, [data]);

  const { type, structure, form_version_id } = (data || {}) as Data;
  const { data: candidateData } = useFetchCandidateDetailsQuery(null, {
    skip: type !== FORM_TYPE.REGISTRATION || default_role !== 'CANDIDATE',
  });

  const candidateDetails: CandidateData = useMemo(() => get(candidateData, 'can_candidate.0', {}), [candidateData]);

  // Both query hooks are called unconditionally (rules-of-hooks); `skip` gates which one
  // actually fetches based on whether a form_instance_id is present. [TAL-11522]
  const formInstanceByIdResult = useGetFormInstanceByIdQuery(
    {
      form_instance_id,
    },
    {
      skip: !form_instance_id,
    },
  );
  const formInstanceByVersionResult = useGetFormInstanceQuery(
    {
      form_version_id,
    },
    {
      skip: !!form_instance_id || !form_version_id,
    },
  );
  const { data: form_instance_data } = (form_instance_id ? formInstanceByIdResult : formInstanceByVersionResult) as {
    data: FieldData;
  };

  const formTypeValue = type || formType;
  const content = structure?.doc || formContent?.doc;
  const btnProps = structure?.button?.props || formContent?.button?.props || { label: '' };

  const { user_id } = getItem('user_details') || {};
  const savedKey = `frm_save_${user_id}_${id}`;

  const setFormData = (fieldValues: FieldData) => dispatch(updateFormData({ savedKey, fieldValues }));

  const setFormFileData = (fieldValues: FieldData) => dispatch(updateFormFileData(fieldValues));

  useEffect(() => {
    dispatch(updateFormState(ROUTE_TYPE.RENDERER));
    initForm(searchParams, initobj);
    const eventMethod = 'addEventListener';
    const eventer = window[eventMethod];
    const messageEvent = 'message';

    eventer(messageEvent, (event) => {
      //Trigger formSubmit from parent (For Recruit)
      if (event.data.type === 'submitEvaluationForm') {
        if (event.data?.model_id) {
          modelId.current = Number(event?.data?.model_id);
        }
        submitButtonRef.current?.click();
      }
    });
    loadModels();
  }, []);

  useEffect(() => {
    if (content) {
      const candidateDetails = get(candidateData, 'can_candidate.0', {});
      const {
        savedContent,
        formData = {},
        formFileData = {},
      } = loadSavedContent(content, form_instance_data, candidateDetails);
      setFormData(formData);
      setFormFileData(formFileData);
      (async () => {
        const { groupedBlocks, instanceSections, conditionalLogics } = await initBlocksRender(
          savedContent,
          setFormFileData,
          setFormData,
          editor,
          logicsRef,
        );
        setGroupedContent(groupedBlocks);
        instanceSectionsRef.current = instanceSections;
        logicsRef.current = conditionalLogics;
      })();
      if (candidateDetails) dispatch(updateCandidateDetails(candidateDetails));
    }
  }, [content, candidateDetails, form_instance_data]);

  useEffect(() => {
    const handleFormReadOnly = () => {
      function preventInteraction(e: Event) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }

      // Handle Input and TextArea elements
      const inputElements = document.querySelectorAll(
        `
          input[type="text"],
          input[type="checkbox"],
          input[type="radio"],
          input[type="number"],
          input[type="email"],
          textarea,
          select,
          [customblocktype="file"]
        `,
      );

      inputElements?.forEach((input) => {
        const el = input as HTMLElement;
        if (isFieldEnabled(el)) {
          const field = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

          // Use `disabled` instead of `readonly`
          field.disabled = isReadOnly;
        }
      });

      // Handle date picker containers
      const datePickerContainers = document.querySelectorAll('.date-picker');
      datePickerContainers.forEach((container) => {
        if (isReadOnly) {
          container.classList.add('pointer-events-none');
          const clearIcon = container.querySelector('[data-testid="clear"]');
          if (clearIcon) {
            clearIcon.classList.add('hidden');
          }
        } else {
          container.classList.remove('pointer-events-none');
          const clearIcon = container.querySelector('[data-testid="clear"]');
          if (clearIcon) {
            clearIcon.classList.remove('hidden');
          }
        }
      });

      // Handle custom radio elements with role="radio"
      const radioRoleElements = document.querySelectorAll('[role="radio"]');
      radioRoleElements.forEach((el) => {
        el.setAttribute('aria-disabled', isReadOnly ? 'true' : 'false');

        if (isReadOnly) {
          el.classList.add('disabled-radio');
          el.addEventListener('click', preventInteraction, true);
          el.addEventListener('keydown', preventInteraction, true);
        } else {
          el.classList.remove('disabled-radio');
          el.removeEventListener('click', preventInteraction, true);
          el.removeEventListener('keydown', preventInteraction, true);
        }
      });
    };

    // Only run if content is rendered
    if (groupedContent.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(handleFormReadOnly, 100);
    }
  }, [searchParams, groupedContent]); // Add groupedContent as dependency

  const [createAuthRequest] = useCreateAuthRequestMutation();

  const onFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    formTypeValue: FormRendererProps['formType'],
  ) => {
    try {
      event?.preventDefault();
      setIsSubmitting(true);

      const hasError = await processFaceAuthDetails(
        faceAuthDetails,
        dispatch,
        createAuthRequest,
        updateFaceAuthDetails,
      );

      if (hasError) return;

      const saveFormattedData = getFormattedSaveData(formData, formFileData);
      const saveFormObj: {
        formFieldsData: any;
        formId: number;
        model?: string | null;
        model_id?: number;
      } = {
        formFieldsData: saveFormattedData,
        formId: Number(id),
      };
      saveFormObj['model'] = model;
      saveFormObj['model_id'] = Number(modelId?.current);
      await saveForm({
        ...saveFormObj,
        saveFormSuccessCallback: (formInstanceId: number) => {
          postFormData(formData, formInstanceId, formTypeValue);
          successSaveFormInstanceNotification();
        },
        saveFormFailureCallback: () => {
          failSaveFormInstanceNotification();
        },
      });
      trackEvent(mixpanelEvents.FORM_SUBMIT);
    } catch (e) {
      sentry.captureException(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const grids = useMemo(
    () =>
      map(groupedContent, ({ htmlBlocks, layout, props }, index) => {
        const { maxInstances } = instanceSectionsRef.current[index] || {};

        const handleInstanceUpdate = (action: 'add' | 'subtract') => {
          const {
            targetItem,
            repeatCount,
            sectionLogics = [],
          } = handleSectionInstances(
            index,
            action,
            groupedContent,
            instanceSectionsRef,
            setFormFileData,
            setFormData,
            logicsRef,
            savedKey,
          ) || {};

          if (props?.attrkey && targetItem && typeof repeatCount === 'number') {
            const instanceKey = `${props.attrkey}_instance`;
            const rootElement = document.getElementById(instanceKey);

            if (rootElement) {
              // Initialize root if not already created
              let root = rootRef.current[instanceKey];
              if (!root) {
                root = ReactDOM.createRoot(rootElement);
                rootRef.current[instanceKey] = root;
              }

              // Determine button disabled states
              const isAddDisabled = maxInstances && repeatCount >= maxInstances;
              const isSubtractDisabled = repeatCount === 0;

              // Update instanceSectionsRef
              instanceSectionsRef.current[index] = {
                ...instanceSectionsRef.current[index],
                instances: repeatCount,
              };

              // Render SectionGroupRender component
              root.render(
                <Provider store={store}>
                  <SectionGroupRender
                    sectionContent={targetItem}
                    deviceType={deviceType}
                    sectionLogics={sectionLogics}
                    isAddDisabled={isAddDisabled}
                    isSubtractDisabled={isSubtractDisabled}
                    savedKey={savedKey}
                    handleInstanceUpdate={handleInstanceUpdate}
                  />
                </Provider>,
              );

              return { repeatCount, htmlBlocks };
            }
          }
        };

        return (
          <div
            className={classNames(props?.border && 'border border-solid mb-4 p-4 rounded-md', props?.hide && 'hidden')}
            id={`${props?.attrkey}_instance_conditional_logic`}
          >
            <div id={`${props?.attrkey}_instance`}>
              {renderContent(htmlBlocks, layout, deviceType)}
              {get(props, 'toggleState.maxInstances') && (
                <>
                  {renderIcon(faSquarePlus as IconProp, typeof maxInstances === 'number' && maxInstances <= 0, () =>
                    handleInstanceUpdate('add'),
                  )}
                  {renderIcon(faSquareMinus as IconProp, true, () => handleInstanceUpdate('subtract'))}
                </>
              )}
            </div>
          </div>
        );
      }),
    [groupedContent, deviceType],
  );

  const isButtonDisabled = useMemo(
    () => some(fileUploadStatus, (val) => val === UPLOAD_STATUS.UPLOADING),
    [fileUploadStatus],
  );

  return (
    <div className="max-h-screen pl-4 pr-4 w-full min-h-screen bg-white block-render">
      {formType && (
        <div className="cursor-pointer w-full flex justify-end p-5 bg-white">
          <div className="mr-3">
            <Toggle
              labelClassNames="text-gray-500"
              label={intl.formatMessage(messages.mobileView)}
              onChange={(checked: boolean) => {
                if (checked) setDeviceType(DEVICE_TYPE.MOBILE);
                else setDeviceType(DEVICE_TYPE.DESKTOP);
              }}
              size="sm"
              checked={deviceType === DEVICE_TYPE.MOBILE}
            />
          </div>
          <div
            role="button"
            tabIndex={0}
            data-testid="back-to-editor"
            onClick={() => setFormState?.(ROUTE_TYPE.BUILDER)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setFormState?.(ROUTE_TYPE.BUILDER);
              }
            }}
            className="text-sm font-light	text-gray-500	"
            title={intl.formatMessage(messages.backToEditor)}
          >
            {/* <FontAwesomeIcon icon={solid('right-from-line')} /> */}
            <FormattedMessage {...messages.backToEditor} />
          </div>
        </div>
      )}
      <div
        className={classNames(
          'mx-auto mt-2 px-1 py-5 sm:px-5 sm:py-10 bg-white overflow-auto',
          formType
            ? 'h-[85vh] max-h-[85vh] ' + (deviceType === DEVICE_TYPE.MOBILE ? 'max-w-sm shadow-lg' : 'max-w-6xl')
            : 'max-h-screen',
        )}
      >
        <form
          onSubmit={(event) => onFormSubmit(event, formTypeValue)}
          id={formTypeValue === FORM_TYPE.REGISTRATION ? 'assessmentForm' : 'evaluationForm'}
          className="flex flex-col h-full"
        >
          <div className="flex-1 overflow-auto no-scrollbar relative">
            {status === 'pending' ? (
              <div className="h-full flex items-center justify-center grow">
                <Loader />
              </div>
            ) : (
              <div className="bn-container">{grids}</div>
            )}
          </div>
          <div className="w-100 flex justify-center mt-4 flex-shrink-0">
            <Button
              ref={submitButtonRef}
              id="submitButton"
              buttonType="submit"
              className={classNames((formTypeValue !== FORM_TYPE.REGISTRATION || isReadOnly) && 'hidden')}
              loading={isSubmitting}
              disabled={isButtonDisabled}
              tooltip={
                isButtonDisabled && {
                  message: 'file upload is in progress.',
                  id: 'form-submit',
                }
              }
            >
              {btnProps?.label || intl.formatMessage(messages.submit)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default memo(BlockNoteRenderer);
